import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import {
  Box,
  Card,
  CardActionArea,
  Button,
  Stack,
  IconButton,
  Badge,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListUl, faFilter } from "@fortawesome/free-solid-svg-icons";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import { Sheet } from "react-modal-sheet";
import { motion, AnimatePresence } from "motion/react";
import ProductCategory from "./ProductCategory";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";

function ProductLists({ selectedCat, setSelectedCat, category_id }) {
  const navigation = useNavigate();
  const location = useLocation();

  const MotionGridItem = motion.create(Grid);
  const MotionGrid = motion.create(Grid);
  const CardMotion = motion.create(Card);

  const [data, setData] = useState([]); // Main product list
  const [imgPath, setImgPath] = useState("");
  const [noImgPath, setNoImgPath] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isOpen, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [selectCheck, setSelectCheck] = useState([]);
  const [filterCount, setFilterCount] = useState("");
  const [hasFilterApplied, setHasFilterApplied] = useState(false);
  const iOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const [page, setPage] = useState(1);

  const loaderRef = useRef(null);
  const resetRef = useRef(false);
  const prevCategoryRef = useRef(null);

  // Framer Motion Variants
  const fadeInVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const cardVariants = {
    initial: { y: 0, scale: 1 },
    hover: {
      y: -6,
      scale: 1.03,
      boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
      transition: {
        duration: 0.4,
        delay: 0.3,
        ease: "easeIn",
      },
    },
  };

  async function fetchProductData(pg, catId, typeList, allowFetch = hasMore) {
    if (loading) return; // ✅ Early exit
    if (!allowFetch && data.length > 0) {
      return;
    }

    if (prevCategoryRef.current !== null && prevCategoryRef.current !== catId) {
      pg = 1;
      typeList = [];
      // important: also reset page state
      setPage(1);
      setData([]); // ensure cleared
    }

    prevCategoryRef.current = catId;

    setLoading(true);

    let url = new URL("https://thesoyara.com/app/index.php?r=api/item-list");
    url.searchParams.append("page_limit", 20);
    url.searchParams.append("page_nos", pg);
    url.searchParams.append("category", catId);
    url.searchParams.append("type", typeList.toString());

    try {
      let response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      let result = await response.json();
      if (result?.item_master?.length > 0) {
        setData((prev) => {
          const prevIds = new Set(prev.map((item) => item.id));
          const newItems = result.item_master.filter(
            (item) => !prevIds.has(item.id)
          );
          return [...prev, ...newItems];
        });
        setImgPath(result.image_def_path);
        setNoImgPath(result.no_image_path);
        setFilterCount(typeList.length);
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      throw new Error("Something went wrong");
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setHasMore(true);
    setData([]);
    setPage(1);
    setSelectedCat([]);
    setSelectCheck([]);
    resetRef.current = true; // Flag set
    // Explicitly fetch first page for new category
    fetchProductData(1, category_id, [], true);
  }, [category_id]);

  useEffect(() => {
    setHasMore(true);
    if (!resetRef.current) {
      setData([]);
      setPage(1);
      fetchProductData(1, category_id, selectedCat, true);
    } else {
      resetRef.current = false; // Don’t run fetch 2nd time if it was triggered by category
    }
  }, [selectedCat]);

  useEffect(() => {
    if (page > 1 && hasMore && !loading) {
      fetchProductData(page, category_id, selectedCat, hasMore);
    }
  }, [page]);

  // Intersection Observer Logic
  const observerCallback = useCallback(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, loading]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0,
      rootMargin: "300px",
    });
    const target = loaderRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
      observer.disconnect();
    };
  }, [observerCallback]);

  function applyFilter() {
    setHasMore(true);
    if (JSON.stringify(selectedCat) !== JSON.stringify(selectCheck)) {
      setSelectedCat(selectCheck);
    }
    setHasFilterApplied(false);
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });

    setData([]);
    setPage(1);
  }

  function cancelFilter() {
    setHasMore(true);
    setSelectCheck([]);
    setHasFilterApplied(false);
    setOpen(false);
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"flex-end"}
        className="product-selected-title"
      >
        <Stack direction="row">
          <IconButton onClick={() => setViewMode("list")}>
            <FontAwesomeIcon
              icon={faListUl}
              className={viewMode === "list" ? "icon-active" : "icon-inactive"}
            />
          </IconButton>
          <IconButton onClick={() => setViewMode("grid")}>
            <GridViewRoundedIcon
              className={`grid-icon ${
                viewMode === "grid" ? "icon-active" : "icon-inactive"
              }`}
            />
          </IconButton>

          <Button
            sx={{
              display: {
                lg: "none",
                md: "none",
                sm: "flex",
                xs: "flex",
                position: "relative",
                zIndex: 9999,
                pointerEvents: "auto",
              },
            }}
            className="filter-btn"
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectCheck([...selectedCat]); // ✅ yahi logic
              setOpen(true);
            }}
            onTouchStart={() => {
              setSelectCheck([...selectedCat]);
              setOpen(true);
            }}
            startIcon={
              <Badge badgeContent={filterCount} color="primary">
                <FontAwesomeIcon icon={faFilter} style={{ fontSize: "22px" }} />
              </Badge>
            }
          >
            Filters
          </Button>
        </Stack>
      </Stack>

      <MotionGrid
        container
        spacing={3}
        className="product-container"
        viewport={{ once: true }}
      >
        <AnimatePresence>
          {data.map((item) => (
            <MotionGridItem
              key={item.id}
              initial={item.isNew ? "hidden" : "visible"}
              animate="visible"
              variants={fadeInVariants}
              exit={{ opacity: 0, y: -10 }}
              size={{
                lg: viewMode === "grid" ? 3 : 12,
                xs: viewMode === "grid" ? 6 : 12,
                sm: viewMode === "grid" ? 6 : 12,
                md: viewMode === "grid" ? 4 : 12,
              }}
            >
              <Box>
                <CardMotion
                  className={`product-card ${viewMode}-view `}
                  onClick={() => navigation(`/product_details/${item.id}`)}
                  variants={cardVariants}
                  initial="initial"
                  whileHover="hover"
                >
                  <CardActionArea>
                    <div className="img-block">
                      <img
                        className="product-img"
                        src={
                          item.images ? `${imgPath}${item.images}` : noImgPath
                        }
                        alt={item.name}
                      />
                    </div>
                  </CardActionArea>
                  <div className="product-details-block">
                    <h3 className="product-title">{item.name}</h3>
                  </div>
                </CardMotion>
              </Box>
            </MotionGridItem>
          ))}
        </AnimatePresence>
        {!loading && data.length === 0 && (
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%", padding: "40px 0" }}
          >
            <DotLottieReact
              src="https://lottie.host/fd9f0226-e194-462e-8c1c-30f2ee59f5a1/eGUuu5tlVc.lottie"
              loop
              autoplay
              style={{ width: "100%", maxWidth: "600px", marginBottom: "16px" }}
            />
            <h3>No products found</h3>
            <p style={{ color: "#888", fontSize: "14px", textAlign: "center" }}>
              Don’t worry, there’s still plenty to discover.
            </p>
            <p style={{ color: "#888", fontSize: "14px", textAlign: "center" }}>
              You can reset the filters or browse other categories.
            </p>
          </Stack>
        )}

        {loading && (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            style={{ width: "100%" }}
          >
            <DotLottieReact
              src="https://lottie.host/e5599654-39d1-4cac-aef5-1c2c4a5af8ca/bTMlPQbvw8.lottie"
              loop
              autoplay
              style={{ width: "60px", height: "60px" }}
            />
          </Stack>
        )}
      </MotionGrid>
      {!loading && hasMore && (
        <div ref={loaderRef} style={{ minHeight: "50px" }} />
      )}
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        disableScrollLock={true}
        disableDiscovery={true}
        disableSwipeToOpen={true}
        disableBackdropTransition={!iOS}
        onClose={() => {
          setOpen(false);
        }}
        onOpen={() => {
          setOpen(true);
        }}
      >
        <div style={{ padding: "16px", maxHeight: "80vh", overflowY: "auto" }}>
          <ProductCategory
            setSelectedCat={setSelectedCat}
            setSelectCheck={setSelectCheck}
            selectedCat={selectedCat}
            flag={1}
            category_id={category_id}
            setHasFilterApplied={setHasFilterApplied}
          />
        </div>
        <div
          className="bottom-sheet-footer"
          style={{
            marginTop: "16px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            className="cancel-btn"
            variant="contained"
            onClick={cancelFilter}
          >
            Cancel
          </Button>
          <Button
            className="apply-btn"
            variant="contained"
            onClick={applyFilter}
          >
            Apply
          </Button>
        </div>
      </SwipeableDrawer>

      {/* <Sheet
        isOpen={isOpen}
        onClose={() => {
          skipFetchRef.current = false;
          setOpen(false);
        }}
      >
        <Sheet.Container>
          <Sheet.Header />
          <Sheet.Content>
            <Sheet.Scroller draggableAt="both">
              <ProductCategory
                setSelectedCat={setSelectedCat}
                setSelectCheck={setSelectCheck}
                selectedCat={selectedCat}
                flag={1}
                category_id={category_id}
                setHasFilterApplied={setHasFilterApplied}
              />
            </Sheet.Scroller>
          </Sheet.Content>
          <div className="bottom-sheet-footer">
            <Button
              className="cancel-btn"
              variant="contained"
              onClick={cancelFilter}
            >
              Cancel
            </Button>
            <Button
              className="apply-btn"
              variant="contained"
              onClick={applyFilter}
            >
              Apply
            </Button>
          </div>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setOpen(false)} />
      </Sheet> */}
    </>
  );
}

export default ProductLists;
